/**
 * ItemsOfTheCourse Component
 *
 * Displays a list of key course items and statistics, such as ratings, enrolled students, lessons, activities, duration, audio, subtitles, coupon, mobile access, and diploma.
 * Passes relevant props to child components for dynamic rendering and state management.
 *
 * Features:
 * - Conditional rendering of positive rating if above 50%
 * - Renders icons and text for each course item
 * - Maps over subtitle data for dynamic rendering
 * - PropTypes and defaultProps for robust type checking
 *
 * @param {Object} props - Component props
 * @param {Object} props.dataCourse - Data object containing course info and statistics
 * @returns {JSX.Element} The rendered course items section
 */
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import MovieOutlinedIcon from '@mui/icons-material/MovieOutlined';
import AttachmentIcon from '@mui/icons-material/Attachment';
import SentimentSatisfiedIcon from '@mui/icons-material/SentimentSatisfied';
import WatchLaterOutlinedIcon from '@mui/icons-material/WatchLaterOutlined';
import VolumeUpOutlinedIcon from '@mui/icons-material/VolumeUpOutlined';
import ClosedCaptionOutlinedIcon from '@mui/icons-material/ClosedCaptionOutlined';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import StayPrimaryPortraitIcon from '@mui/icons-material/StayPrimaryPortrait';
import classes from './ItemsOfTheCourse.module.css';

const ItemsOfTheCourse = props => {
  const { t } = useTranslation(['course']);
  return (
    <aside className={classes.itemsContainer}>
      {/* Conditional rendering: positive rating if above 50% */}
      {parseInt(
        props.dataCourse.aditionalData.positiveRatingPercent
      ) > 50 && (
        <p className={classes.itemDescriptionContainer}>
          <StarBorderIcon className={classes.productIcon} />
          <span className={classes.itemText}>
            {parseInt(
              props.dataCourse.aditionalData.positiveRatingPercent
            )}{' '}
            {t('positive_feedback')} (
            {props.dataCourse.aditionalData.totalReviews})
            {/* Props passed: positive rating percent and total reviews */}
          </span>
        </p>
      )}
      {/* Enrolled students */}
      <p className={classes.itemDescriptionContainer}>
        <GroupOutlinedIcon className={classes.productIcon} />
        <span className={classes.itemText}>
          {t('enrolled_students')}: {props.dataCourse.usersInCourse}
          {/* Props passed: enrolled students count */}
        </span>
      </p>
      {/* Lessons */}
      <p className={classes.itemDescriptionContainer}>
        <MovieOutlinedIcon className={classes.productIcon} />
        <span className={classes.itemText}>
          {props.dataCourse.data.noLecciones} {t('lessons')}
          {/* Props passed: number of lessons */}
        </span>
      </p>
      {/* Activities */}
      <p className={classes.itemDescriptionContainer}>
        <AttachmentIcon className={classes.productIcon} />
        <span className={classes.itemText}>
          {
            props.dataCourse.lessonsCourse.lessonExtraInfo
              .lessonActivities.activities
          }{' '}
          {t('activities')}
          {/* Props passed: number of activities */}
        </span>
      </p>
      {/* Online label */}
      <p className={classes.itemDescriptionContainer}>
        <SentimentSatisfiedIcon className={classes.productIcon} />
        <span className={classes.itemText}>{t('online')}</span>
      </p>
      {/* Duration in hours */}
      <p className={classes.itemDescriptionContainer}>
        <WatchLaterOutlinedIcon className={classes.productIcon} />
        <span className={classes.itemText}>
          {t('duration_of')}{' '}
          {props.dataCourse.lessonsCourse.lessonExtraInfo.lessons
            .seconds /
            60 /
            60}{' '}
          {t('hours')}
          {/* Props passed: duration in hours */}
        </span>
      </p>
      {/* Audio label */}
      <p className={classes.itemDescriptionContainer}>
        <VolumeUpOutlinedIcon className={classes.productIcon} />
        <span className={classes.itemText}>
          {t('audio')}: Español
          {/* Props passed: audio language */}
        </span>
      </p>
      {/* Subtitles (CC) */}
      <p className={classes.itemDescriptionContainer}>
        <ClosedCaptionOutlinedIcon className={classes.productIcon} />
        {props.dataCourse.aditionalData.ccData.map(cc => (
          <span key={cc.description} className={classes.itemText}>
            {cc.description},
            {/* Props passed: subtitle description */}
          </span>
        ))}
      </p>
      {/* Coupon label */}
      <p className={classes.itemDescriptionContainer}>
        <ConfirmationNumberOutlinedIcon
          className={classes.productIcon}
        />
        <span className={classes.itemText}>{t('coupon')}</span>
      </p>
      {/* Mobile access label */}
      <p className={classes.itemDescriptionContainer}>
        <StayPrimaryPortraitIcon className={classes.productIcon} />
        <span className={classes.itemText}>
          {t('mobile_access')}
        </span>
      </p>
      {/* Diploma image and label */}
      <p className={classes.itemDescriptionContainer}>
        <figure className={classes.iconContainer}>
          <img
            className={classes.img} // Prop passed: image styling
            src="https://d1g6c01s9ssd7h.cloudfront.net/img/icon_DiplomaFinalizacion.png" // Prop passed: diploma image URL
          />
        </figure>
        <span className={classes.itemTextWithImage}>
          {t('course_diploma')}
          {/* Props passed: diploma label */}
        </span>
      </p>
    </aside>
  );
};

/**
 * PropTypes definition for type checking and development documentation
 * Provides runtime validation and serves as inline documentation
 */
ItemsOfTheCourse.propTypes = {
  /**
   * Data object containing course info and statistics
   * Passed as prop to render course items
   */
  dataCourse: PropTypes.object
};

ItemsOfTheCourse.defaultProps = {
  dataCourse: {}
};

export default ItemsOfTheCourse;
